import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'
import StatCard from './StatCard'
import './V2Dashboard.css'

// Status pills mirror the workspace sidebar's `ws-status-*` classes so the
// dashboard reads as the same product surface. Labels stay short.
const STATUS_LABELS = {
  DRAFT: 'Draft',
  PENDING_QUOTE: 'Awaiting quotes',
  RFQS_SENT: 'RFQs sent',
  PARTIAL: 'Partial',
  NEEDS_REPLY: 'Needs reply',
  DECIDE: 'Review quotes',
  QUOTED: 'Quotes received',
  QUOTE_CONFIRMED: 'Confirmed',
  BOOKED: 'Booked',
  IN_TRANSIT: 'In transit',
  DELIVERED: 'Delivered'
}

function shortLabel(s) {
  if (!s) return '—'
  // Strip trailing ", CN" / ", US" / etc, keep the city portion.
  return s.split(',')[0].trim().slice(0, 24)
}

function shipmentSummary(shipment) {
  if (shipment.user_name) return shipment.user_name
  const origin = shortLabel(shipment.origin_port || shipment.pickup_city)
  const dest = shortLabel(shipment.destination_port || shipment.destination_address)
  return `${origin || '—'} → ${dest || '—'}`
}

/**
 * Dashboard for the authenticated tenant.
 *
 * Shows live data from /api/v2/me/subscription. Stats that depend on
 * data we don't have yet (shipments, quotes, win rate) render as honest
 * empty states rather than mocked numbers. They'll light up once the
 * Stage 5 tenant-scoped quote pipeline is shipped.
 */
export default function V2Dashboard() {
  const navigate = useNavigate()
  const api = useV2Api()

  const [subscription, setSubscription] = useState(null)
  const [tenant, setTenant] = useState(null)
  const [shipments, setShipments] = useState([])
  const [shipmentsMeta, setShipmentsMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [creatingShipment, setCreatingShipment] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([
      api.getSubscription(),
      api.getTenant(),
      // listShipments is failure-tolerant: a 4xx/5xx here should not block the
      // rest of the dashboard. Swallow per-call errors and treat as "no
      // shipments" so the rest of the page still renders.
      api.listShipments({ limit: 5 }).catch(() => ({ data: [], meta: { total: 0 } }))
    ])
      .then(([subRes, tenantRes, shipmentsRes]) => {
        if (cancelled) return
        setSubscription(subRes?.data || null)
        setTenant(tenantRes?.data || null)
        setShipments(Array.isArray(shipmentsRes?.data) ? shipmentsRes.data : [])
        setShipmentsMeta(shipmentsRes?.meta || null)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err)
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [api])

  if (loading) {
    return (
      <div style={{ padding: '2rem', color: 'var(--text-muted, #666)' }}>
        Loading your dashboard…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        padding: '1.25rem',
        border: '1px solid #8B3A3A',
        background: 'rgba(139,58,58,0.08)',
        borderRadius: 8,
        color: '#8B3A3A'
      }}>
        <strong>Couldn't load your workspace.</strong>
        <div style={{ marginTop: 6, fontSize: 14 }}>{friendlyErrorMessage(error, 'Failed to load dashboard')}</div>
        <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
          If this is your first visit after signup, the Clerk webhook may still be
          provisioning your tenant. Try again in a few seconds.
        </div>
      </div>
    )
  }

  const quotesUsed = subscription?.current_period_usage ?? 0
  const quoteLimit = subscription?.quote_limit ?? 0
  const activeSeats = subscription?.active_seats ?? 0
  const seatLimit = subscription?.seat_limit ?? 0
  const planLabel = subscription?.custom_plan_name || subscription?.plan_tier || 'FREE'

  const shipmentsTotal = shipmentsMeta?.total ?? shipments.length
  const hasShipments = shipmentsTotal > 0 && shipments.length > 0

  // Mirrors the workspace's "+ New shipment" — opens a chat intake session
  // so the user lands in the conversational flow instead of the legacy
  // structured form at /shipments/new. The session id is created up front,
  // then we navigate to /workspace where V2WorkspacePage's session loader
  // picks it up automatically.
  const handleNewShipment = async () => {
    if (creatingShipment) return
    setCreatingShipment(true)
    try {
      await api.createChatSession()
    } catch (err) {
      // Non-fatal — V2WorkspacePage's auto-start effect will spin up a
      // session if none exists. Log only so a transient API blip doesn't
      // dead-end the user.
      console.warn('Failed to pre-create chat session, falling back', err)
    } finally {
      setCreatingShipment(false)
      navigate('/workspace')
    }
  }

  return (
    <div>
      {/* Page Header */}
      <div className="v2-page-header">
        <div>
          <h1 className="v2-page-title">Dashboard</h1>
          <p className="v2-page-subtitle">
            {tenant?.name ? `${tenant.name} — ${planLabel} plan` : `${planLabel} plan`}
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="v2-dashboard-stats">
        <StatCard
          title="Quotes this period"
          value={`${quotesUsed} / ${quoteLimit}`}
          subtitle={`${Math.max(0, quoteLimit - quotesUsed)} remaining`}
        />
        <StatCard
          title="Seats"
          value={`${activeSeats} / ${seatLimit}`}
          subtitle={activeSeats >= seatLimit ? 'At capacity — contact sales' : 'Invite teammates from Settings'}
        />
        <StatCard
          title="Active shipments"
          value={shipmentsTotal > 0 ? String(shipmentsTotal) : '—'}
          subtitle={shipmentsTotal > 0
            ? `Across all statuses`
            : 'Live once you create your first shipment'}
        />
        <StatCard
          title="Win rate"
          value="—"
          subtitle="Needs at least a few quote comparisons"
        />
      </div>

      {/* Recent shipments + activity */}
      <div className="v2-dashboard-body">
        <div className="v2-dashboard-shipments">
          <div className="v2-dashboard-section-header">
            <span className="v2-dashboard-section-title">Recent shipments</span>
            <button
              type="button"
              className="v2-btn v2-btn--primary v2-btn--small"
              onClick={handleNewShipment}
              disabled={creatingShipment}
            >
              {creatingShipment ? 'Opening…' : '+ New shipment'}
            </button>
          </div>
          {hasShipments ? (
            <div className="v2-dashboard-shipments-list">
              {shipments.map((s) => {
                const status = s.status || 'DRAFT'
                const statusLabel = STATUS_LABELS[status] || status
                return (
                  <button
                    key={s.id}
                    type="button"
                    className="ws-list-item v2-dashboard-shipment-row"
                    onClick={() => navigate(`/workspace/${s.id}`)}
                  >
                    <div className="ws-list-row">
                      <span className="ws-list-name" title={shipmentSummary(s)}>
                        {shipmentSummary(s)}
                      </span>
                    </div>
                    <div className="ws-list-meta">
                      <span>{s.quote_type === 'domestic_trucking' ? 'Truck' : 'Ocean'}</span>
                      <span className={`ws-status-pill ws-status-${status}`}>
                        {statusLabel}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          ) : (
            <div style={{
              padding: '2.5rem 1.5rem',
              textAlign: 'center',
              color: 'var(--text-muted, #8F8F85)',
              fontSize: 14
            }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>📦</div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary, #23201C)' }}>
                No shipments yet
              </div>
              <div style={{ marginTop: 4 }}>
                Create your first one to start collecting quotes from your providers.
              </div>
              <button
                className="v2-btn v2-btn--primary"
                onClick={handleNewShipment}
                disabled={creatingShipment}
                style={{ marginTop: 14 }}
              >
                {creatingShipment ? 'Opening…' : 'Create shipment'}
              </button>
            </div>
          )}
        </div>

        <div className="v2-dashboard-activity">
          <div className="v2-dashboard-section-header">
            <span className="v2-dashboard-section-title">Recent activity</span>
          </div>
          <div style={{
            padding: '2rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted, #8F8F85)',
            fontSize: 13
          }}>
            Activity will show here as quotes come in, are compared, and sent.
          </div>
        </div>
      </div>
    </div>
  )
}
